import { Controller } from '@hotwired/stimulus';
import application from '~/stimulus_application';

interface SearchResult {
  type: string;
  label: string;
  markdown: string;
}

/**
 * Stimulus controller for the Marksmith OpenAPI link picker.
 *
 * Provides a search-driven dropdown that lets editors find and insert
 * openapi: protocol links (operations, schemas, groups, categories) into
 * the Marksmith textarea at the current cursor position.
 */
export default class LinkPickerController extends Controller {
  static targets = ['panel', 'results', 'input'];
  static values = {
    url: String,
    delay: { type: Number, default: 300 },
  };

  declare readonly panelTarget: HTMLElement;
  declare readonly resultsTarget: HTMLElement;
  declare readonly inputTarget: HTMLInputElement;
  declare readonly urlValue: string;
  declare readonly delayValue: number;

  private timeout: number | null = null;

  toggle() {
    const isHidden = this.panelTarget.classList.contains('hidden');

    if (isHidden) {
      this.panelTarget.classList.remove('hidden');
      this.inputTarget.focus();
      void this.fetch('');
    } else {
      this.close();
    }
  }

  close() {
    this.panelTarget.classList.add('hidden');
    this.inputTarget.value = '';
    this.resultsTarget.innerHTML = '';
  }

  search() {
    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = window.setTimeout(
      () => void this.fetch(this.inputTarget.value),
      this.delayValue
    );
  }

  select(event: Event) {
    const btn = event.currentTarget as HTMLElement;
    const markdown = btn.dataset.markdown;
    if (!markdown) return;

    const textarea = this.findTextarea();
    if (textarea) this.insertAtCursor(textarea, markdown);

    this.close();
  }

  private async fetch(query: string) {
    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set('q', query);

    try {
      const res = await window.fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const results = (await res.json()) as SearchResult[];
      this.render(results);
    } catch {
      // Network errors silently ignored — picker remains open
    }
  }

  private render(results: SearchResult[]) {
    if (results.length === 0) {
      this.resultsTarget.innerHTML = '<p class="px-3 py-2 text-sm text-neutral-400">No results</p>';
      return;
    }

    this.resultsTarget.innerHTML = results
      .map(
        (r) =>
          `<button type="button"
             class="flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700"
             data-action="click->openapi-ui--link-picker#select"
             data-markdown="${escapeHtml(r.markdown)}">
            <span class="shrink-0 rounded bg-neutral-100 px-1 py-px text-xs font-medium text-neutral-500 dark:bg-neutral-600 dark:text-neutral-300">${escapeHtml(r.type)}</span>
            <span class="truncate text-neutral-800 dark:text-neutral-100">${escapeHtml(r.label)}</span>
          </button>`
      )
      .join('');
  }

  private findTextarea(): HTMLTextAreaElement | null {
    const wrapper = this.element.closest('[data-controller~="marksmith"]');
    return (
      wrapper?.querySelector<HTMLTextAreaElement>('[data-marksmith-target="fieldElement"]') ?? null
    );
  }

  private insertAtCursor(textarea: HTMLTextAreaElement, text: string) {
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? start;
    textarea.value = textarea.value.slice(0, start) + text + textarea.value.slice(end);
    textarea.selectionStart = textarea.selectionEnd = start + text.length;
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
    textarea.focus();
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

application.register('openapi-ui--link-picker', LinkPickerController);
